import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';

import { useTranslation, Trans } from 'react-i18next';
import { AppContext } from '../../context/context';
import {
  listCompanyUsers,
  updateCompanyUser,
  readAxiosDetail,
  type CompanyUser,
} from './api_calls';

import { Icon } from '@liftango/ui-icons';
export default function CompanyUsersTab() {
  const { t } = useTranslation('company');
  const context = useContext(AppContext);
  const currentUserId = context?.userData?.userDetails?.userId || '';

  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Role change dialog
  const [roleTarget, setRoleTarget] = useState<CompanyUser | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);

  // Disable/enable dialog
  const [disableTarget, setDisableTarget] = useState<CompanyUser | null>(null);
  const [disableLoading, setDisableLoading] = useState(false);

  const load = async () => {
    try {
      const data = await listCompanyUsers();
      setUsers(data);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleRoleChange = async () => {
    if (!roleTarget) return;
    setRoleLoading(true);
    try {
      const newRole = roleTarget.company_role === 'company_admin' ? 'member' : 'company_admin';
      const updated = await updateCompanyUser(roleTarget.id, { company_role: newRole });
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setRoleTarget(null);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setRoleLoading(false);
    }
  };

  const handleToggleDisabled = async () => {
    if (!disableTarget) return;
    setDisableLoading(true);
    try {
      const updated = await updateCompanyUser(disableTarget.id, { disabled: !disableTarget.disabled });
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setDisableTarget(null);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setDisableLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const query = search.trim().toLowerCase();
  const visibleUsers = users.filter(
    (u) =>
      u.full_name.toLowerCase().includes(query) ||
      u.email.toLowerCase().includes(query)
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        size="small"
        placeholder={t('users.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <Icon name="magnifying-glass" size={20} style={{ marginRight: 4, color: '#52565e' }} />,
        }}
        sx={{ mb: 2, maxWidth: 360 }}
      />

      {visibleUsers.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {t('users.empty')}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('users.columns.name')}</TableCell>
              <TableCell>{t('users.columns.email')}</TableCell>
              <TableCell>{t('users.columns.role')}</TableCell>
              <TableCell>{t('users.columns.status')}</TableCell>
              <TableCell align="right">{t('users.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleUsers.map((user) => {
              const isSelf = user.id === currentUserId;
              return (
                <TableRow key={user.id}>
                  <TableCell>{user.full_name || '—'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.company_role === 'company_admin' ? t('users.roleLabels.admin') : t('users.roleLabels.member')}
                      size="small"
                      color={user.company_role === 'company_admin' ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.disabled ? t('users.status.disabled') : t('users.status.active')}
                      size="small"
                      color={user.disabled ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {isSelf ? (
                      <Tooltip title={t('users.selfTooltip')}>
                        <span>
                          <Button size="small" disabled>{t('users.actions.changeRole')}</Button>
                          <Button size="small" disabled>{t('users.actions.disable')}</Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <>
                        <Button size="small" onClick={() => setRoleTarget(user)}>
                          {user.company_role === 'company_admin' ? t('users.actions.makeMember') : t('users.actions.makeAdmin')}
                        </Button>
                        <Button
                          size="small"
                          color={user.disabled ? 'success' : 'error'}
                          onClick={() => setDisableTarget(user)}
                        >
                          {user.disabled ? t('users.actions.enable') : t('users.actions.disable')}
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Role change confirmation */}
      <Dialog open={!!roleTarget} onClose={() => setRoleTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('users.roleDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            <Trans
              i18nKey="users.roleDialog.message"
              t={t}
              values={{
                name: roleTarget?.full_name || roleTarget?.email || '',
                fromRole: roleTarget?.company_role === 'company_admin' ? t('users.roleLabels.admin') : t('users.roleLabels.member'),
                toRole: roleTarget?.company_role === 'company_admin' ? t('users.roleLabels.member') : t('users.roleLabels.admin'),
              }}
              components={{ bold: <strong /> }}
            />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleTarget(null)} disabled={roleLoading}>{t('users.roleDialog.cancel')}</Button>
          <Button variant="contained" onClick={() => void handleRoleChange()} disabled={roleLoading}>
            {roleLoading ? <CircularProgress size={18} /> : t('users.roleDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable/enable confirmation */}
      <Dialog open={!!disableTarget} onClose={() => setDisableTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{disableTarget?.disabled ? t('users.disableDialog.enableTitle') : t('users.disableDialog.disableTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {disableTarget?.disabled
              ? t('users.disableDialog.enableMessage', { name: disableTarget?.full_name || disableTarget?.email || '' })
              : t('users.disableDialog.disableMessage', { name: disableTarget?.full_name || disableTarget?.email || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableTarget(null)} disabled={disableLoading}>{t('users.disableDialog.cancel')}</Button>
          <Button
            variant="contained"
            color={disableTarget?.disabled ? 'primary' : 'error'}
            onClick={() => void handleToggleDisabled()}
            disabled={disableLoading}
          >
            {disableLoading ? <CircularProgress size={18} /> : (disableTarget?.disabled ? t('users.disableDialog.enable') : t('users.disableDialog.disable'))}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
